import React from "react";
import { ButtonProps, Form, Input, Radio } from "antd";
import QuickEnum, { deprecatedQuicks } from "../../../enum/QuickEnum";
import { useGlobalStore } from "@/store/hooks";
import ShopType from "@/enum/ShopType";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import QuickNameMap from "@/scenes/OrderManagement/map/QuickNameMap";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import EcSelect from "@/components/EcSelect";
import { EcSelectData } from "@/components/EcSelect/type";
import { ShippingDeadlineShopTypes } from "@/utils/ShopUtils";

interface QuickFieldProps {
    shopId: number;

    tab: OrderTab;
}

export enum OrderQuickSearchType {
    AllMatchQuickCriterion = "AllMatchQuickCriterion",
    AnyMatchQuickCriterion = "AnyMatchQuickCriterion",
}

function quickFieldGroupBy(item) {
    if (
        [
            QuickEnum.QUICK_MERGE_PARENT_ORDER,
            QuickEnum.QUICK_NOT_MERGE_ORDER,
            QuickEnum.QUICK_MANUAL_MERGER,
            QuickEnum.QUICK_SPLIT_CHILD_ORDER,
            QuickEnum.QUICK_HAS_BUYER_REMARK,
            QuickEnum.QUICK_HAS_SELLER_REMARK,
            QuickEnum.QUICK_HAS_ANY_REMARK,
            QuickEnum.QUICK_HAS_NO_REMARK,
            QuickEnum.QUICK_HAS_SUPPLIER_REMARK,
        ].includes(item.value)
    ) {
        return "MERGE_OF_REMARK";
    } else if (
        [
            QuickEnum.QUICK_SINGLE_SKU_SINGLE_PIECE,
            QuickEnum.QUICK_SINGLE_SKU_MULTI_PIECE,
            QuickEnum.QUICK_MULTI_SKU,
            QuickEnum.QUICK_HAS_GIFT,
            QuickEnum.QUICK_PART_SHIP,
            QuickEnum.QUICK_PART_CANCELED,
            QuickEnum.QUICK_WAYBILL_NO_INPUTED,
            QuickEnum.QUICK_WAYBILL_NO_UNINPUTED,
            QuickEnum.QUICK_MULTI_WAYBILL,
            QuickEnum.QUICK_CANCEL_WAYBILL_RECYCLED,
        ].includes(item.value)
    ) {
        return "PRODUCT_OF_PRINTED";
    } else if (
        [
            QuickEnum.QUICK_CASH_DELIVERY,
            QuickEnum.QUICK_HAS_SHIPPING_FEE,
            QuickEnum.QUICK_SHIPPING_FEE,
            QuickEnum.QUICK_COUNTRY_ORDER,
            QuickEnum.QUICK_NO_COUNTRY_ORDER,
            QuickEnum.QUICK_NO_SELF_PICKUP,
            QuickEnum.QUICK_NOT_JD_FULFILLMENT,
            QuickEnum.QUICK_ALI_DELIVERY_GUARANTEE,
        ].includes(item.value)
    ) {
        return "SHIPPING";
    } else if (
        [
            QuickEnum.QUICK_EXPEDITE_DELIVERY,
            QuickEnum.QUICK_REGION_BLACK_DELAY_SHIPPING,
            QuickEnum.QUICK_DELIVERY_TIMEOUT,
            QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE,
            QuickEnum.QUICK_SEND_ABOUT_TO_OVERDUE,
            QuickEnum.QUICK_DESIGNATED_LOGISTICS_COMPANY,
            QuickEnum.QUICK_SHIP_BY_SF,
        ].includes(item.value)
    ) {
        return "TAG";
    }

    return "";
}
interface BaseQuickFieldProps {
    options: EcSelectData<QuickEnum>[];

    buttonProps?: ButtonProps;
}

export const BaseQuickField: React.FC<BaseQuickFieldProps> = React.memo((props) => {
    const { options, buttonProps } = props;
    const orderManagementStore = useOrderManagementStore();

    function handleChange(value: QuickEnum) {
        if (value !== QuickEnum.QUICK_DELIVERY_TIMEOUT) {
            orderManagementStore?.table?.switchShippingDeadlineChecked?.(false);
        }

        if (value !== QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE) {
            orderManagementStore?.table?.switchShippingOverdueChecked?.(false);
        }
    }

    const wrappedOptions = [...options]
        .map((item) => {
            if (deprecatedQuicks.includes(item.value)) {
                return null;
            }

            return item;
        })
        .filter((it) => !!it);

    return (
        <>
            <Form.Item
                noStyle
                hidden
                name="orderQuickSearchType"
                initialValue={OrderQuickSearchType.AllMatchQuickCriterion}
            >
                <Input />
            </Form.Item>

            <Form.Item name="quicks" label={<span className="search-field-label">快捷选单</span>}>
                <EcSelect
                    buttonProps={buttonProps}
                    multiple
                    data={wrappedOptions}
                    onChange={handleChange}
                    groupOptions={{ hideGroupTitle: true, groupBy: quickFieldGroupBy }}
                    footerRender={() => {
                        return (
                            <div>
                                <span className="mr5">多选规则:</span>
                                <Form.Item noStyle name="orderQuickSearchType">
                                    <Radio.Group>
                                        <Radio value={OrderQuickSearchType.AllMatchQuickCriterion}>满足全部选项</Radio>
                                        <Radio value={OrderQuickSearchType.AnyMatchQuickCriterion}>
                                            满足其中一个选项
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </div>
                        );
                    }}
                    disabledItem={({ value }) => {
                        return deprecatedQuicks.includes(value);
                    }}
                />
            </Form.Item>
        </>
    );
});

const QuickField: React.FC<QuickFieldProps> = ({ shopId, tab }) => {
    const { shopMapping } = useGlobalStore();
    const shopType = shopMapping.get(shopId)?.type;

    const isPDDShop = [ShopType.PINDUODUO, ShopType.PDD_FDS].includes(shopType);

    const isJDShopType = [ShopType.JD, ShopType.JD_PURCHASE].includes(shopType);

    const isTbShop = shopType === ShopType.TAOBAO;

    const isAliShop = shopType === ShopType.ALIBABA;

    const isDouDian = shopType === ShopType.DOUDIAN;

    const showQuickHasGift = [
        ShopType.PINDUODUO,
        ShopType.KWAI,
        ShopType.DOUDIAN,
        ShopType.KTT,
        ShopType.DOUDIAN_DROP_SHIPPING_SUPPLIER,
        ShopType.PDD_DROP_SHIPPING_SUPPLIER,
    ].includes(shopType);

    const options: EcSelectData<QuickEnum>[] = [
        /** 合并订单&留言备注 */
        { value: QuickEnum.QUICK_MERGE_PARENT_ORDER, label: QuickNameMap[QuickEnum.QUICK_MERGE_PARENT_ORDER] },
        { value: QuickEnum.QUICK_NOT_MERGE_ORDER, label: QuickNameMap[QuickEnum.QUICK_NOT_MERGE_ORDER] },
        { value: QuickEnum.QUICK_MANUAL_MERGER, label: QuickNameMap[QuickEnum.QUICK_MANUAL_MERGER] },
        { value: QuickEnum.QUICK_SPLIT_CHILD_ORDER, label: QuickNameMap[QuickEnum.QUICK_SPLIT_CHILD_ORDER] },
        { value: QuickEnum.QUICK_HAS_BUYER_REMARK, label: QuickNameMap[QuickEnum.QUICK_HAS_BUYER_REMARK] },
        { value: QuickEnum.QUICK_HAS_SELLER_REMARK, label: QuickNameMap[QuickEnum.QUICK_HAS_SELLER_REMARK] },
        { value: QuickEnum.QUICK_HAS_ANY_REMARK, label: QuickNameMap[QuickEnum.QUICK_HAS_ANY_REMARK] },
        { value: QuickEnum.QUICK_HAS_NO_REMARK, label: QuickNameMap[QuickEnum.QUICK_HAS_NO_REMARK] },
        { value: QuickEnum.QUICK_HAS_SUPPLIER_REMARK, label: QuickNameMap[QuickEnum.QUICK_HAS_SUPPLIER_REMARK] },
        /** 根据商品筛选&软件内是否打印 */
        {
            value: QuickEnum.QUICK_SINGLE_SKU_SINGLE_PIECE,
            label: QuickNameMap[QuickEnum.QUICK_SINGLE_SKU_SINGLE_PIECE],
        },
        {
            value: QuickEnum.QUICK_SINGLE_SKU_MULTI_PIECE,
            label: QuickNameMap[QuickEnum.QUICK_SINGLE_SKU_MULTI_PIECE],
        },
        { value: QuickEnum.QUICK_MULTI_SKU, label: QuickNameMap[QuickEnum.QUICK_MULTI_SKU] },
        showQuickHasGift
            ? { value: QuickEnum.QUICK_HAS_GIFT, label: QuickNameMap[QuickEnum.QUICK_HAS_GIFT] }
            : undefined,
        { value: QuickEnum.QUICK_PART_SHIP, label: QuickNameMap[QuickEnum.QUICK_PART_SHIP] },
        { value: QuickEnum.QUICK_PART_CANCELED, label: QuickNameMap[QuickEnum.QUICK_PART_CANCELED] },
        { value: QuickEnum.QUICK_WAYBILL_NO_INPUTED, label: QuickNameMap[QuickEnum.QUICK_WAYBILL_NO_INPUTED] },
        { value: QuickEnum.QUICK_WAYBILL_NO_UNINPUTED, label: QuickNameMap[QuickEnum.QUICK_WAYBILL_NO_UNINPUTED] },
        { value: QuickEnum.QUICK_MULTI_WAYBILL, label: QuickNameMap[QuickEnum.QUICK_MULTI_WAYBILL] },
        {
            value: QuickEnum.QUICK_CANCEL_WAYBILL_RECYCLED,
            label: QuickNameMap[QuickEnum.QUICK_CANCEL_WAYBILL_RECYCLED],
        },
        /** 订单的发货物流类型 */
        { value: QuickEnum.QUICK_CASH_DELIVERY, label: QuickNameMap[QuickEnum.QUICK_CASH_DELIVERY] },
        { value: QuickEnum.QUICK_SHIPPING_FEE, label: QuickNameMap[QuickEnum.QUICK_SHIPPING_FEE] },
        { value: QuickEnum.QUICK_HAS_SHIPPING_FEE, label: QuickNameMap[QuickEnum.QUICK_HAS_SHIPPING_FEE] },
        { value: QuickEnum.QUICK_COUNTRY_ORDER, label: QuickNameMap[QuickEnum.QUICK_COUNTRY_ORDER] },
        { value: QuickEnum.QUICK_NO_COUNTRY_ORDER, label: QuickNameMap[QuickEnum.QUICK_NO_COUNTRY_ORDER] },
        { value: QuickEnum.QUICK_NO_SELF_PICKUP, label: QuickNameMap[QuickEnum.QUICK_NO_SELF_PICKUP] },
        isJDShopType
            ? { value: QuickEnum.QUICK_NOT_JD_FULFILLMENT, label: QuickNameMap[QuickEnum.QUICK_NOT_JD_FULFILLMENT] }
            : undefined,
        {
            value: QuickEnum.QUICK_ALI_DELIVERY_GUARANTEE,
            label: QuickNameMap[QuickEnum.QUICK_ALI_DELIVERY_GUARANTEE],
        },
        /** 平台根据发货和物流打标的订单 */
        (isPDDShop || isTbShop || isDouDian) && tab === OrderTab.PENDING_SHIPMENT
            ? { value: QuickEnum.QUICK_EXPEDITE_DELIVERY, label: QuickNameMap[QuickEnum.QUICK_EXPEDITE_DELIVERY] }
            : undefined,
        isPDDShop
            ? {
                  value: QuickEnum.QUICK_REGION_BLACK_DELAY_SHIPPING,
                  label: QuickNameMap[QuickEnum.QUICK_REGION_BLACK_DELAY_SHIPPING],
              }
            : undefined,
        ShippingDeadlineShopTypes.includes(shopType)
            ? {
                  value: QuickEnum.QUICK_DELIVERY_TIMEOUT,
                  label: isPDDShop ? "即将发货或揽收超时订单" : QuickNameMap[QuickEnum.QUICK_DELIVERY_TIMEOUT],
              }
            : undefined,
        ShippingDeadlineShopTypes.includes(shopType)
            ? {
                  value: QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE,
                  label: QuickNameMap[QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE],
              }
            : undefined,
        isAliShop
            ? {
                  value: QuickEnum.QUICK_SEND_ABOUT_TO_OVERDUE,
                  label: QuickNameMap[QuickEnum.QUICK_SEND_ABOUT_TO_OVERDUE],
              }
            : undefined,
        isPDDShop
            ? {
                  value: QuickEnum.QUICK_DESIGNATED_LOGISTICS_COMPANY,
                  label: QuickNameMap[QuickEnum.QUICK_DESIGNATED_LOGISTICS_COMPANY],
              }
            : undefined,
        isPDDShop
            ? {
                  value: QuickEnum.QUICK_SHIP_BY_SF,
                  label: QuickNameMap[QuickEnum.QUICK_SHIP_BY_SF],
              }
            : undefined,
    ].filter(Boolean);

    return <BaseQuickField options={options} />;
};

export default React.memo(QuickField);
